import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isDirtyEqual } from 'utils/compares';

const saveFailureMessage = 'Unable to save changes. Please try again.';
const refreshFailureMessage =
  'Changes were saved, but the latest contract information could not be refreshed. Reload the page to confirm the latest values.';

export interface ContractInformationSaveAttempt<TDraft> {
  baseline: Readonly<TDraft>;
  draft: Readonly<TDraft>;
}

export type ContractInformationSaveResult<TDraft> =
  | { accepted: true; baseline?: TDraft }
  | { accepted: false; errors: readonly string[] };

interface ContractInformationFeedback {
  type: 'success' | 'warning';
  message: string;
}

type EditorSession = object;

interface ContractInformationRefreshPolicy {
  authority: 'mutation' | 'source';
  refreshSource: () => Promise<void>;
  refreshDependencies?: readonly (() => Promise<void>)[];
}

interface SaveOperation<TDraft extends object, TConfirmation> {
  session: EditorSession;
  attempt: {
    baseline: TDraft;
    draft: TDraft;
  };
  persist: UseContractInformationEditorOptions<TDraft, TConfirmation>['persist'];
  preflight: UseContractInformationEditorOptions<TDraft, TConfirmation>['preflight'];
  refresh: ContractInformationRefreshPolicy;
  equal: NonNullable<UseContractInformationEditorOptions<TDraft, TConfirmation>['equal']>;
}

interface EditorState<TDraft extends object, TConfirmation> {
  resourceKey: string;
  session: EditorSession;
  baseline?: TDraft;
  draft?: TDraft;
  lastSource?: TDraft;
  phase: 'loading' | 'ready' | 'preflighting' | 'awaiting-confirmation' | 'saving';
  confirmation?: {
    details: TConfirmation;
    operation: SaveOperation<TDraft, TConfirmation>;
  };
  errors: readonly string[];
  feedback?: ContractInformationFeedback;
  warnOnSourceError: boolean;
}

export interface UseContractInformationEditorOptions<TDraft extends object, TConfirmation = never> {
  resourceKey: string;
  source: TDraft | undefined;
  isPending: boolean;
  isError: boolean;
  persist: (
    attempt: ContractInformationSaveAttempt<TDraft>
  ) => Promise<ContractInformationSaveResult<TDraft>>;
  preflight?: (
    attempt: ContractInformationSaveAttempt<TDraft>
  ) => Promise<TConfirmation | undefined>;
  refresh: ContractInformationRefreshPolicy;
  equal?: (left: unknown, right: unknown) => boolean;
}

interface UpdateContractInformationDraft<TDraft> {
  <TField extends keyof TDraft>(field: TField, value: TDraft[TField]): void;
  (update: (current: Readonly<TDraft>) => TDraft): void;
}

export interface ContractInformationEditor<TDraft, TConfirmation> {
  draft: TDraft | undefined;
  isLoading: boolean;
  loadFailed: boolean;
  dirty: boolean;
  saving: boolean;
  confirmation: TConfirmation | undefined;
  errors: readonly string[];
  feedback: ContractInformationFeedback | undefined;
  update: UpdateContractInformationDraft<TDraft>;
  save: () => Promise<void>;
  confirmSave: () => Promise<void>;
  cancelSave: () => void;
  dismissErrors: () => void;
}

const reconcileDraft = <TDraft extends object>(
  previousBaseline: TDraft,
  currentDraft: TDraft,
  incomingBaseline: TDraft,
  equal: (left: unknown, right: unknown) => boolean
): TDraft => {
  const draft = Object.assign({}, currentDraft);

  for (const key of Object.keys(incomingBaseline)) {
    const field = key as keyof TDraft;
    if (equal(currentDraft[field], previousBaseline[field])) {
      draft[field] = incomingBaseline[field];
    }
  }

  return draft;
};

export const useContractInformationEditor = <TDraft extends object, TConfirmation = never>({
  resourceKey,
  source,
  isPending,
  isError,
  persist,
  preflight,
  refresh,
  equal = isDirtyEqual,
}: UseContractInformationEditorOptions<TDraft, TConfirmation>): ContractInformationEditor<
  TDraft,
  TConfirmation
> => {
  const [state, setState] = useState<EditorState<TDraft, TConfirmation>>({
    resourceKey,
    session: {},
    phase: 'loading',
    errors: [],
    warnOnSourceError: false,
  });
  const stateRef = useRef(state);
  const optionsRef = useRef({ persist, preflight, refresh, equal });
  const latestAcceptedSave = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    optionsRef.current = { persist, preflight, refresh, equal };
  }, [equal, persist, preflight, refresh]);

  const updateState = (
    update: (current: EditorState<TDraft, TConfirmation>) => EditorState<TDraft, TConfirmation>
  ) => {
    setState((current) => {
      const next = update(current);
      stateRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (stateRef.current.resourceKey === resourceKey) return;

    const next: EditorState<TDraft, TConfirmation> = {
      resourceKey,
      session: {},
      phase: 'loading',
      errors: [],
      warnOnSourceError: false,
    };
    stateRef.current = next;
    setState(next);
  }, [resourceKey]);

  useEffect(() => {
    if (!source || stateRef.current.resourceKey !== resourceKey) return;

    updateState((current) => {
      if (!current.baseline || !current.draft) {
        return {
          ...current,
          baseline: source,
          draft: source,
          lastSource: source,
          phase: 'ready',
        };
      }

      if (current.lastSource && equal(current.lastSource, source)) return current;

      return {
        ...current,
        baseline: source,
        draft: reconcileDraft(current.baseline, current.draft, source, equal),
        lastSource: source,
        feedback:
          current.feedback?.type === 'warning' && current.warnOnSourceError
            ? undefined
            : current.feedback,
        warnOnSourceError: false,
      };
    });
  }, [equal, resourceKey, source]);

  useEffect(() => {
    if (!isError) return;

    updateState((current) => {
      if (current.resourceKey !== resourceKey || !current.baseline || !current.warnOnSourceError)
        return current;

      return {
        ...current,
        feedback: {
          type: 'warning',
          message: refreshFailureMessage,
        },
      };
    });
  }, [isError, resourceKey]);

  const currentState = state.resourceKey === resourceKey ? state : undefined;
  const renderSession = currentState?.session;

  function update<TField extends keyof TDraft>(field: TField, value: TDraft[TField]): void;
  function update(updateDraft: (current: Readonly<TDraft>) => TDraft): void;
  function update<TField extends keyof TDraft>(
    fieldOrUpdate: TField | ((current: Readonly<TDraft>) => TDraft),
    value?: TDraft[TField]
  ) {
    updateState((current) => {
      if (!current.draft || current.session !== renderSession) return current;

      return {
        ...current,
        draft:
          typeof fieldOrUpdate === 'function'
            ? fieldOrUpdate(current.draft)
            : Object.assign({}, current.draft, { [fieldOrUpdate]: value }),
        errors: [],
        feedback: undefined,
      };
    });
  }

  const isActive = (operation: SaveOperation<TDraft, TConfirmation>) =>
    mounted.current && stateRef.current.session === operation.session;

  const refreshAcceptedSave = async (
    operation: SaveOperation<TDraft, TConfirmation>,
    saveNumber: number
  ) => {
    const refreshes = [
      operation.refresh.refreshSource,
      ...(operation.refresh.refreshDependencies ?? []),
    ].map((refresh) => Promise.resolve().then(refresh));
    const [sourceRefresh] = await Promise.allSettled(refreshes);

    if (
      operation.refresh.authority !== 'source' ||
      !isActive(operation) ||
      latestAcceptedSave.current !== saveNumber
    )
      return;

    if (sourceRefresh.status !== 'rejected') return;

    updateState((current) => ({
      ...current,
      feedback: {
        type: 'warning',
        message: refreshFailureMessage,
      },
    }));
  };

  const persistOperation = async (operation: SaveOperation<TDraft, TConfirmation>) => {
    if (!isActive(operation)) return;

    updateState((current) => ({
      ...current,
      phase: 'saving',
      confirmation: undefined,
      errors: [],
    }));

    let result: ContractInformationSaveResult<TDraft>;

    try {
      result = await operation.persist(operation.attempt);
    } catch {
      if (!isActive(operation)) return;

      updateState((current) => ({
        ...current,
        phase: 'ready',
        errors: [saveFailureMessage],
      }));
      return;
    }

    if (!isActive(operation)) return;

    if (!result.accepted) {
      updateState((current) => ({
        ...current,
        phase: 'ready',
        errors: result.errors,
      }));
      return;
    }

    const savedDraft = result.baseline ?? operation.attempt.draft;
    const saveNumber = ++latestAcceptedSave.current;

    updateState((current) => ({
      ...current,
      baseline: savedDraft,
      draft: current.draft
        ? reconcileDraft(operation.attempt.draft, current.draft, savedDraft, operation.equal)
        : savedDraft,
      phase: 'ready',
      feedback: {
        type: 'success',
        message: 'Changes saved successfully',
      },
      warnOnSourceError: operation.refresh.authority === 'source',
    }));

    void refreshAcceptedSave(operation, saveNumber);
  };

  const save = async () => {
    const current = stateRef.current;
    if (
      current.phase !== 'ready' ||
      !current.baseline ||
      !current.draft ||
      optionsRef.current.equal(current.baseline, current.draft)
    )
      return;

    const operation: SaveOperation<TDraft, TConfirmation> = {
      session: current.session,
      attempt: {
        baseline: current.baseline,
        draft: current.draft,
      },
      ...optionsRef.current,
    };

    if (operation.preflight) {
      updateState((editorState) => ({ ...editorState, phase: 'preflighting', errors: [] }));

      try {
        const confirmation = await operation.preflight(operation.attempt);
        if (!isActive(operation)) return;

        if (confirmation !== undefined) {
          updateState((editorState) => ({
            ...editorState,
            phase: 'awaiting-confirmation',
            confirmation: { details: confirmation, operation },
          }));
          return;
        }
      } catch {
        if (!isActive(operation)) return;

        updateState((editorState) => ({
          ...editorState,
          phase: 'ready',
          errors: [saveFailureMessage],
        }));
        return;
      }
    }

    await persistOperation(operation);
  };

  const confirmSave = async () => {
    const operation = stateRef.current.confirmation?.operation;
    if (!operation || !isActive(operation)) return;

    await persistOperation(operation);
  };

  const cancelSave = () => {
    updateState((current) =>
      current.session === renderSession
        ? { ...current, phase: 'ready', confirmation: undefined }
        : current
    );
  };

  const dismissErrors = () => {
    updateState((current) =>
      current.session === renderSession ? { ...current, errors: [] } : current
    );
  };

  const initialized = Boolean(currentState?.baseline && currentState.draft);
  const dirty = Boolean(
    currentState?.baseline &&
    currentState.draft &&
    !equal(currentState.baseline, currentState.draft)
  );

  return {
    draft: currentState?.draft,
    isLoading: isPending && !initialized,
    loadFailed: isError && !initialized,
    dirty,
    saving:
      currentState?.phase === 'saving' ||
      currentState?.phase === 'preflighting' ||
      currentState?.phase === 'awaiting-confirmation',
    confirmation: currentState?.confirmation?.details,
    errors: currentState?.errors ?? [],
    feedback: currentState?.feedback,
    update,
    save,
    confirmSave,
    cancelSave,
    dismissErrors,
  };
};
